/**
 * Compact readout for year-count sliders and fields (e.g. "12 yrs", "1 yr").
 */
export function formatYearsCount(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${n} ${n === 1 ? 'yr' : 'yrs'}`;
}

/**
 * Spaced suffix after a numeric hero value in result panels (" yr" / " yrs").
 */
export function yearsHeroSuffix(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return ' yrs';
  return x === 1 ? ' yr' : ' yrs';
}

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Magnitude rounding for readable calculator outputs:
 * - 0–999 → nearest 10 (43→40, 97→100, 674→670, 999→1000)
 * - 1,000–9,999 → nearest 100 (1356→1400, 9875→9900)
 * - 10,000–99,999 → nearest 1,000
 * - and so on (each decade uses a step 100× smaller than the decade floor)
 */
export function roundForDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return n;
  if (n === 0) return 0;
  const sign = n < 0 ? -1 : 1;
  const abs = Math.abs(n);

  let step;
  if (abs < 1000) {
    step = 10;
  } else {
    const decade = Math.floor(Math.log10(abs)); // 3 for 1k–9.9k, 4 for 10k–99k, …
    step = 10 ** (decade - 1);
  }

  return sign * Math.round(abs / step) * step;
}

/** @typedef {'rounded' | 'precise'} CalcAmountDisplayMode */

/**
 * Resolve a raw amount to the integer used for display (and amount-in-words).
 * @param {number} value
 * @param {CalcAmountDisplayMode} [mode='rounded']
 */
export function resolveAmountForDisplay(value, mode = 'rounded') {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  if (mode === 'precise') return Math.round(n);
  return roundForDisplay(n);
}

/**
 * Format an INR amount for calculator result outputs.
 * @param {number} value
 * @param {CalcAmountDisplayMode} [mode='rounded']
 */
export function formatInrDisplay(value, mode = 'rounded') {
  return formatterINR.format(resolveAmountForDisplay(value, mode));
}

export { formatterINR as calculatorInrFormatter };
